<script setup lang="tsx">
import { ref, onBeforeMount, watch, computed } from 'vue'
import http from '@/services/http'
import type { Response, ApproachingDueDateResponse } from '@/models/api-generated'
import { formatDate, generateDueDateParams, hasPermission } from '@/services/utils'
import Pagination from '@/components/widgets/PaginationComponent.vue'
import { PermOwnMeasureRead, PermAllMeasureRead } from '@/models/enums-generated'
import type { PaginationState } from '@/types/page'

const currentUser = sessionMap.user
const props = defineProps({
	day: String,
	days: String,
	today: String,
	overdue: String,
})

const result = ref<ApproachingDueDateResponse>()
const currentPage = ref<PaginationState>({ page: 1, limit: 10 })
const canViewMethods = computed(() => hasPermission(PermOwnMeasureRead, PermAllMeasureRead))

onBeforeMount(async () => {
	await fetchMethods()
})

async function fetchMethods() {
	if (!canViewMethods.value) {
		result.value = { methods: [], totalPages: 0 }
		return
	}
	const { data } = await http.get<Response<ApproachingDueDateResponse>>(`api/dashboard/approachingDueDate/${currentPage.value.page}`)
	result.value = data.data
}

function goToMethodsPage(row: any) {
	const params = new URLSearchParams({
		responsible: JSON.stringify({ id: row.responsibleId, type: row.responsibleType }),
	})
	params.append('instance', `${row.instanceId}`)
	params.append('reference', `${row.name}`)

	const dueDateStatus = row.class.split('-')[0]
	const dueDateParams = generateDueDateParams(dueDateStatus)
	for (const [key, value] of dueDateParams.entries()) {
		params.append(key, value)
	}

	window.location.href = `/app/methods?${params}`
}

watch(currentPage, fetchMethods, { deep: true })
</script>

<template>
	<p class="text-center fw-bold">
		{{ $t('dashboard.approachingDueDate') }}
		<template v-if="hasPermission(PermOwnMeasureRead) && !hasPermission(PermAllMeasureRead)">
			- {{ $t('user.label') }} {{ currentUser.firstname }} {{ currentUser.lastname }}</template
		>
		<template v-else-if="!canViewMethods"> - {{ $t('roles.missingRights') }}</template>
	</p>

	<div v-if="result" class="container">
		<div
			v-for="(row, index) in result.methods"
			:key="index"
			:class="['row mt-2 rounded py-2 clickable-row', row.class]"
			@click="goToMethodsPage(row)"
		>
			<div class="col-md">
				<div class="row text-break text-colored">
					<div class="col-3">
						{{ row.name }}
					</div>
					<div class="col-6">
						{{ row.responsible }}
					</div>
					<div class="col-3">
						{{ formatDate(row.dueDate) }}
					</div>
				</div>
				<div class="row small text-colored text-break">
					<div class="col">
						{{ row.instance }}
					</div>
					<div class="col-3">
						<span v-if="row.days < 0" class="fw-bold">{{ props.overdue }}</span>
						<span v-else-if="row.days === 0" class="fw-bold">{{ props.today }}</span>
						<span v-else-if="row.days === 1" class="fw-bold">1 {{ props.day }}</span>
						<span v-else>{{ row.days }} {{ props.days }}</span>
					</div>
				</div>
			</div>
		</div>
		<Pagination v-model="currentPage" :total-pages="result.totalPages ?? 0" :disableLimitSelect="true" />
	</div>

	<div v-else class="p-4 text-center">
		<div class="spinner-border text-primary"></div>
	</div>
</template>

<style lang="sass" scoped>
.clickable-row
  cursor: pointer
  transition: background-color 0.2s ease, box-shadow 0.2s ease
  border-radius: 5px

.clickable-row:hover
  filter: brightness(1.4)
  box-shadow: 4px 4px 12px rgba(0, 0, 0, 0.4)
  transition: all 0.2s ease-in-out
</style>
